import React, { useState, useEffect } from 'react';
import { checkGeofenceAccess, getGeofenceMessage } from './GeofenceService';
import './GeofenceLogin.css';

const GeofenceLogin = ({ onSuccess, onError, language = 'ru' }) => {
  const [loading, setLoading] = useState(false);
  const [geofenceStatus, setGeofenceStatus] = useState(null);
  const [formData, setFormData] = useState({
    full_name: '',
    phone: ''
  });
  const [position, setPosition] = useState(null);

  const translations = {
    kk: {
      header: '👤 Алтай қаласының тұрғындары үшін кіру',
      subheader: 'Геопозиция бойынша автоматты кіру',
      requireGeofence: 'Қолданбаға кіру үшін Алтай қаласында екеніңізді растау қажет',
      checkLocation: '📍 Геопозицияны тексеру',
      checking: '📍 Орналасуды анықтау...',
      checkGeolocationFirst: 'Алдымен геолокацияға қолжетімділікті тексеріңіз',
      fillAllFields: 'Барлық өрістерді толтырыңыз',
      loginError: 'Кіру қатесі',
      fullName: 'Сіздің аты-жөніңіз',
      namePlaceholder: 'Иван Иванов',
      phone: 'Телефон нөмірі',
      phonePlaceholder: '+7 777 123 45 67',
      phoneHint: 'Өтінімдерді өңдеу кезінде кері байланыс үшін',
      loggingIn: '⏳ Жүйеге кіру...',
      loginButton: '🚀 Қолданбаға кіру',
      retryButton: '🔄 Қайта тексеру',
      infoRadius: '💡 Кіру үшін Алтай қаласының орталығынан 5км радиуста болу қажет',
      infoPrivacy: 'Сіздің деректеріңіз тек кері байланыс үшін пайдаланылады'
    },
    ru: {
      header: '👤 Вход для жителей города Алтай',
      subheader: 'Автоматический вход по геопозиции',
      requireGeofence: 'Для входа в приложение необходимо подтвердить, что вы находитесь в городе Алтай',
      checkLocation: '📍 Проверить геопозицию',
      checking: '📍 Определяем местоположение...',
      checkGeolocationFirst: 'Сначала проверьте доступ к геолокации',
      fillAllFields: 'Заполните все поля',
      loginError: 'Ошибка входа',
      fullName: 'Ваше имя и фамилия',
      namePlaceholder: 'Иван Иванов',
      phone: 'Номер телефона',
      phonePlaceholder: '+7 777 123 45 67',
      phoneHint: 'Для обратной связи при обработке ваших обращений',
      loggingIn: '⏳ Вход в систему...',
      loginButton: '🚀 Войти в приложение',
      retryButton: '🔄 Проверить заново',
      infoRadius: '💡 Для входа необходимо находиться в радиусе 5км от центра города Алтай',
      infoPrivacy: 'Ваши данные будут использованы только для обратной связи'
    }
  };

  const t = translations[language];

  const checkLocationAccess = async () => {
    setLoading(true);
    try {
      const result = await checkGeofenceAccess();
      setGeofenceStatus(result);
      
      if (result.success && result.isInside) {
        setPosition(result.position);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Geofence check failed:', error);
      setLoading(false);
      if (onError) {
        onError(error);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!position) {
      alert(t.checkGeolocationFirst);
      return;
    }

    if (!formData.full_name.trim() || !formData.phone.trim()) {
      alert(t.fillAllFields);
      return;
    }

    setLoading(true);
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      const response = await fetch(`${backendUrl}/api/auth/geofence-login`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          full_name: formData.full_name.trim(),
          phone: formData.phone.trim(),
          latitude: position.lat,
          longitude: position.lng
        })
      });

      const data = await response.json();

      if (response.ok) {
        // Токен сохранён сервером в httpOnly cookie
        if (onSuccess) {
          onSuccess(data);
        }
      } else {
        const errorMsg = data.detail?.message || data.detail || t.loginError;
        alert(errorMsg);
      }
    } catch (error) {
      console.error('Login error:', error);
      alert(language === 'kk'
        ? 'Жүйеге кіру мүмкін болмады. Интернет қосылымын тексеріңіз.'
        : 'Не удалось войти в систему. Проверьте подключение к интернету.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const formatPhoneNumber = (phone) => {
    // Простое форматирование телефона для Казахстана
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.startsWith('8')) {
      return '+7' + cleaned.slice(1);
    } else if (cleaned.startsWith('7')) {
      return '+' + cleaned;
    }
    return phone;
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setFormData(prev => ({
      ...prev,
      phone: formatted
    }));
  };

  const renderGeofenceStatus = () => {
    if (!geofenceStatus) return null;

    const message = getGeofenceMessage(geofenceStatus, language);
    
    return (
      <div className={`geofence-status ${message.type}`}>
        <div className="status-icon">
          {message.type === 'success' && '✅'}
          {message.type === 'blocked' && '🚫'}
          {message.type === 'error' && '⚠️'}
        </div>
        <div className="status-content">
          <h3>{message.title}</h3>
          <p>{message.message}</p>
          <small>{message.details}</small>
        </div>
      </div>
    );
  };

  const canShowForm = geofenceStatus && geofenceStatus.success && geofenceStatus.isInside;

  return (
    <div className="geofence-login">
      <div className="login-header">
        <h2>{t.header}</h2>
        <p>{t.subheader}</p>
      </div>

      {/* Проверка геопозиции */}
      {!geofenceStatus && (
        <div className="geolocation-check">
          <p>{t.requireGeofence}</p>
          <button 
            onClick={checkLocationAccess}
            disabled={loading}
            className="check-location-btn"
          >
            {loading ? t.checking : t.checkLocation}
          </button>
        </div>
      )}

      {/* Статус геозоны */}
      {renderGeofenceStatus()}

      {/* Форма входа - показывается только если в геозоне */}
      {canShowForm && (
        <form onSubmit={handleSubmit} className="resident-form">
          <div className="form-group">
            <label htmlFor="full_name">{t.fullName}</label>
            <input
              type="text"
              id="full_name"
              name="full_name"
              value={formData.full_name}
              onChange={handleInputChange}
              placeholder={t.namePlaceholder}
              required
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="phone">{t.phone}</label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handlePhoneChange}
              placeholder={t.phonePlaceholder}
              required
              disabled={loading}
            />
            <small>{t.phoneHint}</small>
          </div>

          <button 
            type="submit" 
            disabled={loading}
            className="submit-btn"
          >
            {loading ? t.loggingIn : t.loginButton}
          </button>
        </form>
      )}

      {/* Кнопка повторной проверки */}
      {geofenceStatus && (!geofenceStatus.success || !geofenceStatus.isInside) && (
        <button 
          onClick={checkLocationAccess}
          disabled={loading}
          className="retry-btn"
        >
          {t.retryButton}
        </button>
      )}

      <div className="geofence-info">
        <small>
          {t.infoRadius}
          <br />
          {t.infoPrivacy}
        </small>
      </div>
    </div>
  );
};

export default GeofenceLogin;